import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchConfiguratorConfig } from '@/store/slices/configurator-config';
import { selectActiveBrandGamificationItems } from '@/store/slices/casino-instance';

import { DEFAULT_LANG } from '@/nova/utils/catalog';

import { activeGamificationFeatures, featuresLoadingFrom, type GamificationFeature } from './gamification-sources';

export interface UseGamificationFeatures {
  features: GamificationFeature[];
  /** The configurator catalogue has not resolved yet (the brand purchases are already in the store). */
  loading: boolean;
}

/**
 * The brand's ACTIVE gamification features for the picker: the `/configurator` catalogue joined with the
 * brand's purchases (both from the shared store; the catalogue is fetched once here if a Product page has
 * not already). Only enabled (non-off tier) features are returned.
 */
export const useGamificationFeatures = (): UseGamificationFeatures => {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const lang = i18n.language || DEFAULT_LANG;

  const catalog = useAppSelector((state) => state.configuratorConfig.gamificationItems);
  const loaded = useAppSelector((state) => state.configuratorConfig.loaded);
  const loading = useAppSelector((state) => state.configuratorConfig.loading);
  const error = useAppSelector((state) => state.configuratorConfig.error);
  const purchases = useAppSelector(selectActiveBrandGamificationItems);
  const requestedRef = React.useRef(false);

  React.useEffect(() => {
    if (loaded || loading || requestedRef.current) return;
    requestedRef.current = true;
    void dispatch(fetchConfiguratorConfig());
  }, [loaded, loading, dispatch]);

  const features = React.useMemo(
    () => activeGamificationFeatures(catalog, purchases, lang),
    [catalog, purchases, lang],
  );

  // A failed read is settled (see featuresLoadingFrom), so the video editor's mount gate never waits forever.
  return { features, loading: featuresLoadingFrom({ loaded, fetching: loading, error }) };
};

export default useGamificationFeatures;
